import { useState, useMemo, useEffect } from 'react'
import { useApi } from '@/hooks/useApi'
import type { BuzonRemitente } from '@/lib/types'
import { Modal, Button, FormField } from '@/components/ui'
import { IconSend, IconAlertTriangle } from '@/components/icons/Icons'

/*
  Abrir un hilo nuevo desde el Buzón de Equipo.

  A diferencia de `NuevoCorreoModal` (el de las fichas), acá el destinatario se
  escribe: el candado de ese otro es la lista de contactos del cliente, y una
  bandeja de equipo no tiene esa lista — escribirle a alguien nuevo es
  justamente para lo que sirve. Lo que queda en su lugar es la confirmación
  antes de enviar y que la ruta sea solo para admin.
*/
interface Props {
  open: boolean
  remitentes: BuzonRemitente[]
  onClose: () => void
  onEnviado: (hiloId: number) => void
}

const EMAIL = /^[\w.+-]+@[\w.-]+\.\w+$/

/** Escapa el texto del editor antes de armar el HTML del correo. */
function aHtml(texto: string): string {
  return texto
    .split('\n')
    .map((l) => l.replace(/[<>&]/g, (ch) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[ch]!)))
    .join('<br>')
}

export default function NuevoCorreoBuzon({ open, remitentes, onClose, onEnviado }: Props) {
  const api = useApi()
  const [remitenteId, setRemitenteId] = useState<number | null>(null)
  const [para, setPara] = useState('')
  const [asunto, setAsunto] = useState('')
  const [cuerpo, setCuerpo] = useState('')
  const [confirmando, setConfirmando] = useState(false)
  const [enviando, setEnviando] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // El id se fija al abrir, no al enviar: así un segundo clic manda el mismo id
  // y el backend lo reconoce en vez de mandar el correo dos veces.
  const envioId = useMemo(
    () => `n-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    [open],  // eslint-disable-line react-hooks/exhaustive-deps
  )

  useEffect(() => {
    if (!open) return
    setRemitenteId(remitentes[0]?.id ?? null)
    setPara(''); setAsunto(''); setCuerpo('')
    setError(null); setConfirmando(false)
  }, [open, remitentes])

  const destinatarios = useMemo(
    () => para.split(/[,;]/).map((d) => d.trim()).filter(Boolean),
    [para],
  )
  const invalidos = destinatarios.filter((d) => !EMAIL.test(d))
  const listo = !!remitenteId && destinatarios.length > 0 && invalidos.length === 0
    && !!asunto.trim() && !!cuerpo.trim()

  async function enviar() {
    setEnviando(true)
    setError(null)
    try {
      const r = await api.post<{ hilo_id: number }>('/api/buzon/enviar', {
        envio_id: envioId,
        remitente_id: remitenteId,
        para: destinatarios,
        asunto: asunto.trim(),
        cuerpo_html: aHtml(cuerpo),
      })
      onClose()
      onEnviado(r.hilo_id)
    } catch (e: any) {
      setError(e?.message || 'No se pudo enviar el correo.')
      setConfirmando(false)
    } finally {
      setEnviando(false)
    }
  }

  const remitente = remitentes.find((r) => r.id === remitenteId)

  return (
    <Modal open={open} onClose={onClose} title="Escribir un correo" width={620}>
      {remitentes.length === 0 ? (
        <p className="text-muted">
          No hay remitentes activos. Se dan de alta en Configuración → Buzón.
        </p>
      ) : (
        <>
          <FormField label="Desde" hint="El cliente ve este nombre en su bandeja.">
            <select
              className="form-select"
              value={remitenteId ?? ''}
              onChange={(e) => setRemitenteId(Number(e.target.value))}
            >
              {remitentes.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.nombre_mostrar} &lt;{r.email}&gt;{r.cargo ? ` · ${r.cargo}` : ''}
                </option>
              ))}
            </select>
          </FormField>

          <FormField label="Para" hint="Varias direcciones separadas por coma.">
            <input
              type="text"
              className="form-input"
              placeholder="cliente@empresa.com, otro@empresa.com"
              value={para}
              onChange={(e) => setPara(e.target.value)}
            />
          </FormField>

          <FormField label="Asunto">
            <input
              type="text"
              className="form-input"
              value={asunto}
              onChange={(e) => setAsunto(e.target.value)}
            />
          </FormField>

          <FormField label="Mensaje">
            <textarea
              className="form-input"
              rows={8}
              placeholder="Escribí el mensaje…"
              value={cuerpo}
              onChange={(e) => setCuerpo(e.target.value)}
              style={{ width: '100%', resize: 'vertical' }}
            />
          </FormField>

          {remitente?.firma_html
            ? <p className="text-muted" style={{ fontSize: 12.5 }}>Se agrega la firma de {remitente.nombre_mostrar}.</p>
            : <p className="text-muted" style={{ fontSize: 12.5 }}>
                {remitente?.nombre_mostrar} todavía no tiene firma cargada: el correo sale sin ella.
              </p>}

          {invalidos.length > 0 && (
            <p style={{ color: 'var(--danger)', fontSize: 13, marginTop: 8 }}>
              <IconAlertTriangle size={13} /> Dirección inválida: {invalidos.join(', ')}
            </p>
          )}
          {error && (
            <p style={{ color: 'var(--danger)', fontSize: 13, marginTop: 8 }}>
              <IconAlertTriangle size={13} /> {error}
            </p>
          )}

          {confirmando ? (
            <div style={{ marginTop: 14, padding: 12, border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
              <p style={{ fontSize: 14, marginBottom: 4 }}>
                <b>Se va a enviar a:</b> {destinatarios.join(', ')}
              </p>
              <p className="text-muted" style={{ fontSize: 13, marginBottom: 10 }}>
                Sale como <b>{remitente?.nombre_mostrar}</b> &lt;{remitente?.email}&gt; y no se puede deshacer.
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button onClick={enviar} disabled={enviando}>
                  <IconSend size={14} /> {enviando ? 'Enviando…' : 'Confirmar y enviar'}
                </Button>
                <button className="btn btn-secondary" onClick={() => setConfirmando(false)} disabled={enviando}>
                  Volver a editar
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <Button onClick={() => setConfirmando(true)} disabled={!listo}>
                <IconSend size={14} /> Revisar y enviar
              </Button>
              <button className="btn btn-secondary" onClick={onClose}>Cancelar</button>
            </div>
          )}
        </>
      )}
    </Modal>
  )
}
